import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ReactNode, useState, useEffect } from "react";
import LoginPage from "@/pages/login/LoginPage";
import { TokenManager } from "@/lib/axios";
import { PublicRoute } from "@/providers/router/PublicRoute";
import { ProtectedLayout } from "@/components/layout/ProtectedLayout";
import ItemCodePage from "@/pages/standard/ItemCode";
import CompanyCodePage from "@/pages/standard/CompanyCode";
import CommonCodePage from "@/pages/standard/CommonCode";
import OrderPage from "@/pages/dispatch/Order";
import SalesPage from "@/pages/dispatch/Sales";
import MemoPage from "@/pages/additional/Memo";
import SalesByClientPage from "@/pages/settlement/SalesByClient";
import CompanyInfoPage from "@/pages/setting/CompanyInfo";
import WorkPermissionsPage from "@/pages/setting/WorkPermissions";
import MemberPage from "@/pages/standard/EmployeeCode";
import DriverCodePage from "@/pages/standard/DriverCode";
import VehicleCodePage from "@/pages/standard/VehicleCode";
import ReportPage from "@/pages/performance/Report";
import SalesAnalysisPage from "@/pages/performance/SalesAnalysis";
import HazardousPage from "@/pages/management/hazardous";
import FreightPage from "@/pages/management/freight";
import MonthlyTransactionsPage from "@/pages/settlement/monthlyTransactions";
import MonthlyTransportPage from "@/pages/settlement/monthlyTransport";
import DailySalesByClientPage from "@/pages/settlement/DailySalesByClient";
import PurchaseByDriverPage from "@/pages/settlement/PurchaseByDriver";
import SalesTransactionByClientPage from "@/pages/settlement/SalesTransactionByClient";
import DailyTransportPage from "@/pages/settlement/DailyTransport";
import UnitPriceCodePage from "@/pages/standard/UnitPriceCode";
import OrderDelPage from "@/pages/dispatch/OrderDel";
import SalesDelPage from "@/pages/dispatch/SalesDel";
import LocationPage from "@/pages/location/LocationPage";
import PrivacyPage from "@/pages/PrivacyPage";

interface RouterProviderProps {
  children?: ReactNode;
}

export function RouterProvider({ children }: RouterProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // 🔄 앱 초기화 시 토큰 상태 확인 및 자동 갱신
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const tokenValid = await TokenManager.refreshTokenIfNeeded();
        setIsAuthenticated(tokenValid);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* 🌐 개인정보 처리방침 - 완전 공개 페이지 (인증 무관) */}
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* 🔓 로그인 페이지 - 공개 페이지 */}
        <Route
          path="/login"
          element={
            <PublicRoute isAuthenticated={isAuthenticated} isInitialized={isInitialized}>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* 🔐 보호된 페이지들 - Header를 포함한 레이아웃 */}
        <Route path="/*" element={<ProtectedLayout isAuthenticated={isAuthenticated} isInitialized={isInitialized} />}>
          {/* 환경설정 페이지들 */}
          <Route path="system/companyInfo" element={<CompanyInfoPage />} />
          <Route path="system/workPermissions" element={<WorkPermissionsPage />} />
          {/* 기준관리 페이지들 */}
          <Route path="standard/itemCode" element={<ItemCodePage />} />
          <Route path="standard/companyCode" element={<CompanyCodePage />} />
          <Route path="standard/commonCode" element={<CommonCodePage />} />
          <Route path="standard/employeeCode" element={<MemberPage />} />
          <Route path="standard/driverCode" element={<DriverCodePage />} />
          <Route path="standard/vehicleCode" element={<VehicleCodePage />} />
          <Route path="standard/unitPriceCode" element={<UnitPriceCodePage />} />
          {/* 배차관리 페이지들 */}
          <Route path="dispatch/order" element={<OrderPage />} />
          <Route path="dispatch/sales" element={<SalesPage />} />
          <Route path="dispatch/orderDel" element={<OrderDelPage />} />
          <Route path="dispatch/salesDel" element={<SalesDelPage />} />
          {/* 정산관리 페이지들 */}
          <Route path="settlement/salesByClient" element={<SalesByClientPage />} />
          <Route path="settlement/dailySalesByClient" element={<DailySalesByClientPage />} />
          <Route path="settlement/purchaseByDriver" element={<PurchaseByDriverPage />} />
          <Route path="settlement/transactionsByClient" element={<SalesTransactionByClientPage />} />
          <Route path="settlement/monthlyTransactions" element={<MonthlyTransactionsPage />} />
          <Route path="settlement/monthlyTransport" element={<MonthlyTransportPage />} />
          <Route path="settlement/dailyTransport" element={<DailyTransportPage />} />
          {/* 관리대장 페이지들 */}
          <Route path="management/hazardous" element={<HazardousPage />} />
          <Route path="management/freight" element={<FreightPage />} />
          {/* 실적관리 페이지들 */}
          <Route path="performance/report" element={<ReportPage />} />
          <Route path="performance/salesAnalysis" element={<SalesAnalysisPage />} />
          {/* 부가기능 페이지들 */}
          <Route path="additional/memo" element={<MemoPage />} />
          {/* 관제시스템 */}
          <Route path="location" element={<LocationPage />} />
          {/* 즐겨찾기 페이지들 */}
        </Route>
      </Routes>
      {children}
    </Router>
  );
}
